import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PawPrint, Trash2, Plus, Save, Calendar as CalendarIcon, Utensils, Droplets, Moon, Mail } from "lucide-react";

import api from "@/lib/api";
import { useDogs } from "@/context/DogsContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";

// Client-side new_id — server also assigns one on save, but during the
// day the events live only in local state so we need a stable key.
const cid = () => (crypto?.randomUUID?.() || `id_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

const DEMEANOR = ["calm","happy","relaxed","curious","cautious","anxious","fearful","proud","shy","energetic","tired","overstimulated"];
const STOOL = ["firm", "soft", "loose", "other"];
const NAP_LOCS = ["crate","floor","lap","pen","outside","car","other"];
const ACTIVITY_SECTIONS = [
  { key: "exercise", label: "Exercise", placeholder: "e.g. 10-min sniff walk in yard" },
  { key: "socialization", label: "Socialization", placeholder: "e.g. met 3 new people, calm" },
  { key: "new_experience", label: "New experience", placeholder: "e.g. first ride in car" },
  { key: "grooming", label: "Grooming", placeholder: "e.g. face brush, 5 min" },
  { key: "table_training", label: "Table training", placeholder: "e.g. 3 min on grooming table" },
  { key: "stacking", label: "Stacking", placeholder: "e.g. free-stack rewarded 4x" },
];

const today = () => new Date().toISOString().slice(0, 10);
const nowHM = () => new Date().toTimeString().slice(0, 5);

function Chip({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
        selected
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background text-foreground border-border hover:bg-secondary/60"
      }`}
    >
      {children}
    </button>
  );
}

function EventList({ items, onRemove, render, emptyLabel, testid }) {
  if (!items?.length) {
    return <p className="text-xs text-muted-foreground italic" data-testid={`${testid}-empty`}>{emptyLabel}</p>;
  }
  return (
    <ul className="divide-y divide-border rounded-md border border-border overflow-hidden text-sm" data-testid={testid}>
      {items.map((e) => (
        <li key={e.id} className="flex items-start justify-between gap-2 p-2">
          <div className="min-w-0 flex-1">{render(e)}</div>
          <button
            type="button"
            onClick={() => onRemove(e.id)}
            className="shrink-0 text-muted-foreground hover:text-destructive p-1"
            data-testid={`${testid}-remove-${e.id}`}
            aria-label="Remove"
          >
            <Trash2 size={13} />
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function PuppyLogPage() {
  const { activeDog } = useDogs();
  const [logDate, setLogDate] = useState(today());
  const [log, setLog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Send-weekly-recap-email button state (see puppy_digest.py backend).
  const [sendingDigest, setSendingDigest] = useState(false);
  // Last 7 days of daily logs — powers the streak strip + yesterday-recap card.
  const [recent, setRecent] = useState([]);

  // Section-specific quick-add form state.
  const [mealForm, setMealForm] = useState({ time: nowHM(), food: "", amount: "", note: "" });
  const [pottyForm, setPottyForm] = useState({ time: nowHM(), kind: "pee", location: "outside", note: "" });
  const [napForm, setNapForm] = useState({ start: nowHM(), duration: 30, location: "crate", note: "" });
  const [activityForms, setActivityForms] = useState(() =>
    Object.fromEntries(ACTIVITY_SECTIONS.map((s) => [s.key, { description: "", note: "" }]))
  );

  useEffect(() => {
    if (!activeDog?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/puppy-log/${activeDog.id}`, { params: { date: logDate } });
        setLog(data);
        setDirty(false);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load log");
      }
    })();
  }, [activeDog?.id, logDate]);

  // Recent-logs strip. Only fetched when the dog changes (not on every date
  // hop) so scrubbing the date picker is snappy. Re-fetched after each save.
  const refreshRecent = async () => {
    if (!activeDog?.id) return;
    try {
      const { data } = await api.get(`/puppy-log/${activeDog.id}/recent`, { params: { days: 7 } });
      setRecent(data?.items || []);
    } catch (err) {
      console.debug("puppy-log recent fetch failed", err);
    }
  };
  useEffect(() => { refreshRecent(); }, [activeDog?.id]);

  const isPuppy = activeDog?.dog_type === "puppy";

  const mutate = (patch) => {
    setLog((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const appendEvent = (field, event) => {
    setLog((prev) => ({ ...prev, [field]: [...(prev[field] || []), { ...event, id: cid() }] }));
    setDirty(true);
  };
  const removeEvent = (field, id) => {
    setLog((prev) => ({ ...prev, [field]: (prev[field] || []).filter((e) => e.id !== id) }));
    setDirty(true);
  };
  const appendActivity = (section, event) => {
    setLog((prev) => ({
      ...prev,
      activities: {
        ...(prev.activities || {}),
        [section]: [...((prev.activities || {})[section] || []), { ...event, id: cid() }],
      },
    }));
    setDirty(true);
  };
  const removeActivity = (section, id) => {
    setLog((prev) => ({
      ...prev,
      activities: {
        ...(prev.activities || {}),
        [section]: ((prev.activities || {})[section] || []).filter((e) => e.id !== id),
      },
    }));
    setDirty(true);
  };
  const toggleDemeanor = (m) => {
    setLog((prev) => {
      const list = prev?.demeanor || [];
      const next = list.includes(m) ? list.filter((x) => x !== m) : [...list, m];
      return { ...prev, demeanor: next };
    });
    setDirty(true);
  };

  const save = async () => {
    if (!log) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/puppy-log/${activeDog.id}/${logDate}`, log);
      setLog(data);
      setDirty(false);
      toast.success("Saved");
      refreshRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendWeeklyDigestTest = async () => {
    setSendingDigest(true);
    try {
      await api.post("/puppy-log/digest/send-test");
      toast.success("Recap emailed — check your inbox");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send recap");
    } finally {
      setSendingDigest(false);
    }
  };

  const ageDisplay = useMemo(() => log?.age_display || "", [log?.age_display]);

  // Streak strip: last 7 days (oldest → newest). A day is "logged" if the
  // recent list contains it AND the log has at least one event or a demeanor
  // pick — a bare-created empty day shouldn't count.
  const streakDays = useMemo(() => {
    const map = new Map();
    for (const r of recent) map.set(r.log_date, r);
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const r = map.get(iso);
      const hasContent = !!r && (
        (r.demeanor?.length || 0) > 0 ||
        (r.meals?.length || 0) > 0 ||
        (r.potties?.length || 0) > 0 ||
        (r.naps?.length || 0) > 0 ||
        !!r.notes || !!r.stool
      );
      days.push({ iso, logged: hasContent, label: d.toLocaleDateString(undefined, { weekday: "short" })[0] });
    }
    return days;
  }, [recent]);

  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const iso = d.toISOString().slice(0, 10);
    return recent.find((r) => r.log_date === iso) || null;
  }, [recent]);

  if (!activeDog) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-muted-foreground">Pick a dog to start logging.</p>
      </div>
    );
  }
  if (!isPuppy) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <SectionTitle overline="Puppy log" title={`${activeDog.name} isn't in Puppy mode`} />
        <p className="text-sm text-muted-foreground">
          The Daily Puppy Log is only available for dogs set to <strong>Puppy mode</strong>. Switch modes from the dog&apos;s Overview page (pencil icon) and come back.
        </p>
      </div>
    );
  }
  if (!log) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="puppy-log-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <SectionTitle overline="Puppy log" title={`${activeDog.name} — ${logDate}`} icon={PawPrint} />
          {ageDisplay ? (
            <p className="text-sm text-muted-foreground mt-1" data-testid="puppy-log-age">Age on this day: <strong>{ageDisplay}</strong></p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <CalendarIcon size={14} className="text-muted-foreground" />
          <Input
            type="date"
            value={logDate}
            onChange={(e) => setLogDate(e.target.value)}
            data-testid="puppy-log-date"
            className="w-auto"
          />
          <Button onClick={save} disabled={saving || !dirty} data-testid="puppy-log-save">
            <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* 7-day streak strip. Filled dots = logged (any content), hollow = missed. */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground" data-testid="puppy-log-streak">
        <span className="uppercase tracking-[0.18em] font-semibold">Last 7 days</span>
        <div className="flex items-center gap-1.5">
          {streakDays.map((d) => (
            <button
              type="button"
              key={d.iso}
              onClick={() => setLogDate(d.iso)}
              title={d.iso}
              data-testid={`streak-dot-${d.iso}`}
              className={`w-6 h-6 rounded-full text-[10px] font-medium flex items-center justify-center border transition-colors ${
                d.iso === logDate
                  ? "ring-2 ring-primary ring-offset-1 border-primary"
                  : ""
              } ${
                d.logged
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:bg-secondary/60"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
        <span className="ml-auto flex items-center gap-3" data-testid="streak-count">
          <span>{streakDays.filter((d) => d.logged).length}/7 logged</span>
          <button
            type="button"
            onClick={sendWeeklyDigestTest}
            disabled={sendingDigest}
            title="Email me a snapshot of the last 7 days"
            data-testid="puppy-digest-send"
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border hover:bg-secondary/60 text-xs text-foreground/85 disabled:opacity-50"
          >
            <Mail size={12} />
            {sendingDigest ? "Sending…" : "Email me the recap"}
          </button>
        </span>
      </div>

      {/* Yesterday-at-a-glance recap — only shown when we actually have a log
          for yesterday, so day 1 users don't see an empty band. */}
      {yesterday ? (
        <Card className="border-border shadow-none rounded-lg bg-secondary/30" data-testid="yesterday-recap">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                Yesterday at a glance
              </p>
              <button
                type="button"
                onClick={() => setLogDate(yesterday.log_date)}
                className="text-xs text-primary hover:underline"
                data-testid="yesterday-open"
              >
                Open →
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2" data-testid="yesterday-meals">
                <Utensils size={14} className="text-muted-foreground" />
                <span><strong>{yesterday.meals?.length || 0}</strong> meals</span>
              </div>
              <div className="flex items-center gap-2" data-testid="yesterday-potties">
                <Droplets size={14} className="text-muted-foreground" />
                <span><strong>{yesterday.potties?.length || 0}</strong> potty</span>
              </div>
              <div className="flex items-center gap-2" data-testid="yesterday-naps">
                <Moon size={14} className="text-muted-foreground" />
                <span><strong>{yesterday.naps?.length || 0}</strong> naps</span>
              </div>
            </div>
            {yesterday.demeanor?.length ? (
              <p className="text-xs text-muted-foreground mt-2">
                Mood: {yesterday.demeanor.slice(0, 4).join(", ")}
                {yesterday.demeanor.length > 4 ? "…" : ""}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Demeanor */}
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-5 space-y-3">
          <h3 className="font-medium">How did they feel today?</h3>
          <div className="flex flex-wrap gap-2">
            {DEMEANOR.map((m) => (
              <Chip
                key={m}
                selected={log.demeanor?.includes(m)}
                onClick={() => toggleDemeanor(m)}
                testid={`demeanor-${m}`}
              >
                {m}
              </Chip>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Meals */}
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-5 space-y-3">
          <h3 className="font-medium">Meals</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Input type="time" value={mealForm.time} onChange={(e) => setMealForm({ ...mealForm, time: e.target.value })} data-testid="meal-time" />
            <Input placeholder="Food" value={mealForm.food} onChange={(e) => setMealForm({ ...mealForm, food: e.target.value })} data-testid="meal-food" />
            <Input placeholder="Amount (½ cup)" value={mealForm.amount} onChange={(e) => setMealForm({ ...mealForm, amount: e.target.value })} data-testid="meal-amount" />
            <Button
              type="button"
              onClick={() => {
                if (!mealForm.food.trim()) { toast.error("Add food to log a meal"); return; }
                appendEvent("meals", { ...mealForm });
                setMealForm({ time: nowHM(), food: "", amount: "", note: "" });
              }}
              data-testid="meal-add"
            >
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
          <EventList
            items={log.meals}
            onRemove={(id) => removeEvent("meals", id)}
            emptyLabel="No meals logged yet."
            testid="meals-list"
            render={(e) => (
              <span className="text-sm">
                <strong>{e.time}</strong> · {e.food}{e.amount ? ` — ${e.amount}` : ""}
              </span>
            )}
          />
        </CardContent>
      </Card>

      {/* Potties + stool */}
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-5 space-y-4">
          <h3 className="font-medium">Potty log</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Input type="time" value={pottyForm.time} onChange={(e) => setPottyForm({ ...pottyForm, time: e.target.value })} data-testid="potty-time" />
            <select className="border border-border rounded-md px-2 h-9 text-sm bg-background" value={pottyForm.kind} onChange={(e) => setPottyForm({ ...pottyForm, kind: e.target.value })} data-testid="potty-kind">
              <option value="pee">Pee</option>
              <option value="poo">Poo</option>
              <option value="both">Both</option>
              <option value="accident">Accident</option>
            </select>
            <Input placeholder="Location (yard)" value={pottyForm.location} onChange={(e) => setPottyForm({ ...pottyForm, location: e.target.value })} data-testid="potty-location" />
            <Input placeholder="Note" value={pottyForm.note} onChange={(e) => setPottyForm({ ...pottyForm, note: e.target.value })} />
            <Button
              type="button"
              onClick={() => {
                appendEvent("potties", { ...pottyForm });
                setPottyForm({ time: nowHM(), kind: "pee", location: pottyForm.location, note: "" });
              }}
              data-testid="potty-add"
            >
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
          <EventList
            items={log.potties}
            onRemove={(id) => removeEvent("potties", id)}
            emptyLabel="No potty events yet."
            testid="potties-list"
            render={(e) => (
              <span className="text-sm">
                <strong>{e.time}</strong> · {e.kind}{e.location ? ` @ ${e.location}` : ""}{e.note ? ` — ${e.note}` : ""}
              </span>
            )}
          />

          <div className="pt-2 border-t border-border">
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Stool consistency (daily)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {STOOL.map((s) => (
                <Chip key={s} selected={log.stool === s} onClick={() => mutate({ stool: s === log.stool ? "" : s })} testid={`stool-${s}`}>
                  {s}
                </Chip>
              ))}
            </div>
            <Input
              placeholder="Note (colour, mucus, etc.)"
              value={log.stool_note || ""}
              onChange={(e) => mutate({ stool_note: e.target.value })}
              className="mt-2"
              data-testid="stool-note"
            />
          </div>
        </CardContent>
      </Card>

      {/* Naps */}
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-5 space-y-3">
          <h3 className="font-medium">Naps</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Input type="time" value={napForm.start} onChange={(e) => setNapForm({ ...napForm, start: e.target.value })} data-testid="nap-start" />
            <Input type="number" min={1} placeholder="Minutes" value={napForm.duration} onChange={(e) => setNapForm({ ...napForm, duration: Number(e.target.value) || 0 })} data-testid="nap-duration" />
            <select className="border border-border rounded-md px-2 h-9 text-sm bg-background" value={napForm.location} onChange={(e) => setNapForm({ ...napForm, location: e.target.value })} data-testid="nap-location">
              {NAP_LOCS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <Input placeholder="Note" value={napForm.note} onChange={(e) => setNapForm({ ...napForm, note: e.target.value })} />
            <Button
              type="button"
              onClick={() => {
                if (!napForm.duration) { toast.error("Add a duration"); return; }
                appendEvent("naps", { ...napForm });
                setNapForm({ start: nowHM(), duration: 30, location: napForm.location, note: "" });
              }}
              data-testid="nap-add"
            >
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
          <EventList
            items={log.naps}
            onRemove={(id) => removeEvent("naps", id)}
            emptyLabel="No naps logged yet."
            testid="naps-list"
            render={(e) => (
              <span className="text-sm">
                <strong>{e.start}</strong> · {e.duration} min in {e.location}{e.note ? ` — ${e.note}` : ""}
              </span>
            )}
          />
        </CardContent>
      </Card>

      {/* Activities: exercise, socialization, new_experience, grooming, table_training, stacking */}
      {ACTIVITY_SECTIONS.map((section) => (
        <Card key={section.key} className="border-border shadow-none rounded-lg">
          <CardContent className="p-5 space-y-3">
            <h3 className="font-medium">{section.label}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
              <Input
                placeholder={section.placeholder}
                value={activityForms[section.key].description}
                onChange={(e) => setActivityForms((f) => ({ ...f, [section.key]: { ...f[section.key], description: e.target.value } }))}
                data-testid={`activity-${section.key}-desc`}
              />
              <Input
                placeholder="Note"
                value={activityForms[section.key].note}
                onChange={(e) => setActivityForms((f) => ({ ...f, [section.key]: { ...f[section.key], note: e.target.value } }))}
              />
              <Button
                type="button"
                onClick={() => {
                  const v = activityForms[section.key];
                  if (!v.description.trim()) { toast.error(`Describe the ${section.label.toLowerCase()}`); return; }
                  appendActivity(section.key, { time: nowHM(), description: v.description.trim(), note: v.note.trim() });
                  setActivityForms((f) => ({ ...f, [section.key]: { description: "", note: "" } }));
                }}
                data-testid={`activity-${section.key}-add`}
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <EventList
              items={log.activities?.[section.key] || []}
              onRemove={(id) => removeActivity(section.key, id)}
              emptyLabel={`No ${section.label.toLowerCase()} entries yet.`}
              testid={`activity-${section.key}-list`}
              render={(e) => (
                <span className="text-sm">
                  <strong>{e.time || ""}</strong> {e.description}{e.note ? ` — ${e.note}` : ""}
                </span>
              )}
            />
          </CardContent>
        </Card>
      ))}

      {/* Freeform notes */}
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-5 space-y-3">
          <h3 className="font-medium">End-of-day notes</h3>
          <Textarea
            rows={4}
            value={log.notes || ""}
            onChange={(e) => mutate({ notes: e.target.value })}
            placeholder="Anything you want to remember about today…"
            data-testid="puppy-log-notes"
            maxLength={2000}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !dirty} data-testid="puppy-log-save-bottom">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>
    </div>
  );
}
